import { Skeletons, RM0 } from './kit';
import { commissionColor, commissionTypeLabel, isTenancyLine, ratePct } from '../lib/commission';

/**
 * How a branch sale's commission splits, for its Centre Director — read-only.
 *
 * `split` is the sale's commission breakdown as the server sends it with the
 * branch sale (`commission_split`): every line, the same payload the Earnings deal
 * sheet reads, so a line reads the same here as it does for the agents on the
 * deal. `undefined` while loading; `null` when the split could not be read.
 * Shared by the phone page and the desktop page.
 */
export default function BranchSplit({ split }) {
  if (split === undefined) return <Skeletons n={3} height={54} />;
  if (split === null) return <Note>Couldn&rsquo;t load the split just now. Try again in a moment.</Note>;

  const items = Array.isArray(split.items) ? split.items : [];
  // Lines HQ keyed by hand sit outside net GCI and outside the total, so they
  // get their own group and total, as on the Earnings deal sheet. Tenancy lines
  // too: they are the agent's cut of the tenant's stamping fee, not commission,
  // and the server totals them apart (`tenancy_amount`), as the admin tab does.
  const splitItems = items.filter((l) => !l.is_manual && !isTenancyLine(l));
  const tenancyItems = items.filter(isTenancyLine);
  const manualItems = items.filter((l) => l.is_manual);
  const residual = Number(split.balance) || 0;
  // An ERP ledger is history as keyed, and some over-pay their net GCI; a
  // negative "company share" there is an artefact, not money the company owes.
  const showResidual = Math.abs(residual) >= 0.01 && !(split.is_legacy && residual < 0);

  return (
    <div data-tour="branch-split" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {split.is_preview && (
        <div style={{ background: 'color-mix(in srgb,var(--warn) 8%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 32%,var(--border))', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--warn)' }}>ESTIMATE · NOT FINAL</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 3 }}>
            Worked out from the sale as it stands. It becomes final when HQ raises the invoice.
          </div>
        </div>
      )}

      {splitItems.length === 0 ? (
        <Note>
          {split.preview_unavailable
            ? 'The split can’t be worked out just now. Try again in a moment.'
            : 'The split is worked out when HQ raises the invoice for this sale.'}
        </Note>
      ) : (
        <>
          <Lines rows={splitItems} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 2px' }}>
            <Sum label={`Split between ${splitItems.length} ${splitItems.length === 1 ? 'party' : 'parties'}`} amount={split.total_amount} />
            {showResidual && (
              <Sum label={split.is_legacy ? 'Company share (not itemised in ERP)' : 'Company share'} amount={residual} />
            )}
            {Number(split.net_gci) > 0 && <Sum label="Net GCI" amount={split.net_gci} strong />}
          </div>
        </>
      )}

      {tenancyItems.length > 0 && (
        <>
          <Group label="TENANCY FEE" note="Paid by the tenant, outside net GCI" />
          <Lines rows={tenancyItems} />
          <div style={{ padding: '0 2px' }}>
            <Sum label="Tenancy fee total" amount={split.tenancy_amount ?? tenancyItems.reduce((t, l) => t + (Number(l.amount) || 0), 0)} />
          </div>
        </>
      )}

      {manualItems.length > 0 && (
        <>
          <Group label="ADDED BY HQ" note="Outside net GCI" />
          <Lines rows={manualItems} />
        </>
      )}
    </div>
  );
}

function Lines({ rows }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      {rows.map((l, i) => {
        const rate = ratePct(l.rate);
        const paid = Number(l.paid_amount) || 0;
        return (
          <div key={l.id || i} data-tour="branch-split-line" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', background: commissionColor(l.commission_type) }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {commissionTypeLabel(l.commission_type)}{rate ? ` · ${rate}` : ''}
                </span>
                {l.is_manual && l.description && (
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.description}</span>
                )}
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.recipient_name}</span>
              </span>
            </span>
            <span style={{ textAlign: 'right', flex: 'none' }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM0(l.amount)}</span>
              {paid > 0 && <span style={{ display: 'block', fontSize: 11, color: 'var(--ok, #2F9E6F)', fontVariantNumeric: 'tabular-nums' }}>Paid {RM0(paid)}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const Group = ({ label, note }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
    <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' }}>{label}</span>
    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{note}</span>
  </div>
);

const Sum = ({ label, amount, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: strong ? 'var(--text)' : 'var(--muted)', fontWeight: strong ? 700 : 400 }}>
    <span>{label}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{RM0(amount)}</span>
  </div>
);

const Note = ({ children }) => <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{children}</div>;
